import { AnyAction, AsyncThunk, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from '@nextgen-web-framework/core';
import type {
  InitialCartState,
  ChangeQtyCartType,
  AddToCartReqPayloadType,
  CartIdReqPayloadType,
  ProjectionReqPayloadType,
  CreateCartPayloadType,
} from '../../types';
import { cartApi } from '../requests/cartApi';
import { OrderType, SubCartDetail, type Cart, type ErrorResponseSubDetailsInner } from '../models';
import { CartAPIResponse, CartIdWithMultipleEntryIdType, SubCartIdReqPayloadType } from '../../types/cartTypes';

type GenericAsyncThunk = AsyncThunk<
  CartAPIResponse,
  unknown,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.startsWith('commerceCart') && action.type.endsWith('/pending');
}

function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.startsWith('commerceCart') && action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.startsWith('commerceCart') && action.type.endsWith('/rejected');
}

export const getCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  CartIdReqPayloadType & ProjectionReqPayloadType & SubCartIdReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/getCart', ({ cartId, projections, subCartId }) =>
  cartApi.getSubCart({
    cartId,
    subCartId,
    projections,
  }),
);

export const getSubCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  CartIdReqPayloadType & SubCartIdReqPayloadType & ProjectionReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/getSubCart', ({ cartId, subCartId, projections }) =>
  cartApi.getSubCart({
    cartId,
    subCartId,
    projections,
    options: {
      skipCartIdStoreInLS: true,
      skipCartResStoreInLS: true,
      skipVersionStoreInLS: true,
    },
  }),
);

export const getMiniCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  CartIdReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/getMiniCart', ({ cartId }) =>
  cartApi.getMiniCart({
    cartId,
  }),
);

export const createCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  CreateCartPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/createCart', ({ payload }) =>
  cartApi.createCart({
    payload,
  }),
);

export const changeQtyCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  // First argument to the payload creator
  ChangeQtyCartType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/changeQtyCart', ({ cartId, entryId, payload }) =>
  cartApi.changeQtyForEntryFromCart({
    cartId,
    entryId,
    payload,
  }),
);

export const removeEntryFromCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  // First argument to the payload creator
  CartIdWithMultipleEntryIdType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/removeEntryFromCart', ({ cartId, entryId }) =>
  cartApi.removeEntryFromCart({
    cartId,
    entryId,
  }),
);

export const addToCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  // First argument to the payload creator
  AddToCartReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/addToCart', ({ cartId, payload }) =>
  cartApi.addToCart({
    cartId,
    payload,
  }),
);

export const deleteCartSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  // First argument to the payload creator
  CartIdReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/deleteCart', ({ cartId }) =>
  cartApi.deleteCart({
    cartId,
  }),
);

export const clearCartEntriesSliceReq = createAsyncThunk<
  // Return type of the payload creator
  CartAPIResponse,
  // First argument to the payload creator
  CartIdReqPayloadType,
  {
    dispatch: AppDispatch; // this is store.dispatch
    state: InitialCartState; // this is
  }
>('commerceCart/clearCartEntries', ({ cartId }) =>
  cartApi.clearCartEntries({
    cartId,
  }),
);

const initialState: InitialCartState = {
  cart: {
    id: '',
    amwayUser: {
      amwayUserId: '',
    },
    version: 0,
    orderType: OrderType.Regular,
    subCarts: [],
    metaData: {
      createdAt: new Date('2024-01-10T12:12:00Z'),
    },
  },
  cartCheckoutData: {
    subCarts: [],
  },
  isLoading: true,
  hasError: false,
  hasCartIdInStore: false,
  error: {},
  isBlockerException: false,
  isCheckoutDisabled: true,
  redirectToHomePage: false,
};

export const cartSlice = createSlice({
  name: 'commerceCart',
  initialState,
  reducers: {
    resetCartSlice: () => initialState,
    hasCartIdInStore: (state, action: PayloadAction<boolean>) => {
      state.hasCartIdInStore = action.payload;
    },
    updateCart: (state, action: PayloadAction<Cart>) => {
      state.cart = action.payload;
    },
    updateCartCheckoutData: (
      state,
      action: PayloadAction<{
        subCarts: SubCartDetail[];
      }>,
    ) => {
      state.cartCheckoutData = action.payload;
    },
    updateIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateBlockerException: (state, action: PayloadAction<boolean>) => {
      state.isBlockerException = action.payload;
    },
    updateIsCheckoutDisabled: (state, action: PayloadAction<boolean>) => {
      state.isCheckoutDisabled = action.payload;
    },
    updateRedirectionToHomePage: (state, action: PayloadAction<boolean>) => {
      state.redirectToHomePage = action.payload;
    },
  },
  extraReducers: (builder) => {
    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialCartState, action: PayloadAction<unknown>) => {
      state.hasError = true;
      state.isLoading = false;
      state.error = action as ErrorResponseSubDetailsInner;
    });
    builder.addMatcher(isFulfilledAction, (state: InitialCartState, action: PayloadAction<unknown>) => {
      state.hasError = false;
      state.cart = action.payload as Cart;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialCartState) => {
      state.hasError = false;
    });
  },
});

export const {
  hasCartIdInStore,
  resetCartSlice,
  updateCart,
  updateCartCheckoutData,
  updateIsLoading,
  updateBlockerException,
  updateIsCheckoutDisabled,
  updateRedirectionToHomePage,
} = cartSlice.actions;
